/**
 * StudySync Analytics Service
 * Centralized PostHog analytics integration for consistent event tracking
 */

import posthog from 'posthog-js';

// Event name constants for type safety
export const AnalyticsEvents = {
  // Authentication events
  USER_SIGNED_IN: 'user_signed_in',
  USER_SIGNED_UP: 'user_signed_up',
  USER_SIGNED_OUT: 'user_signed_out',

  // Study management events
  STUDY_CREATED: 'study_created',
  STUDY_CODE_COPIED: 'study_code_copied',
  PARTICIPANTS_INVITED: 'participants_invited',
  COLLABORATORS_INVITED: 'collaborators_invited',
  PARTICIPANT_DELETED: 'participant_deleted',
  TRACKING_ID_EDITED: 'tracking_id_edited',
  CSV_EXPORT_REQUESTED: 'csv_export_requested',

  // Data extraction events
  EXTRACTION_DATA_SOURCE_SELECTED: 'extraction_data_source_selected',
  EXTRACTION_DATE_RANGE_SELECTED: 'extraction_date_range_selected',
  EXTRACTION_PARTICIPANTS_SELECTED: 'extraction_participants_selected',
  EXTRACTION_DATA_TYPES_SELECTED: 'extraction_data_types_selected',
  EXTRACTION_PREVIEW_OPENED: 'extraction_preview_opened',
  EXTRACTION_STARTED: 'extraction_started',
  EXTRACTION_JOB_COMPLETED: 'extraction_job_completed',
  EXTRACTION_JOB_FAILED: 'extraction_job_failed',
  EXTRACTION_BUCKET_ACCESSED: 'extraction_bucket_accessed',

  // Participant enrollment events
  PARTICIPANT_STUDY_CODE_ENTERED: 'participant_study_code_entered',
  PARTICIPANT_CONTACT_SUBMITTED: 'participant_contact_submitted',
  PARTICIPANT_VERIFICATION_SENT: 'participant_verification_sent',
  PARTICIPANT_VERIFICATION_COMPLETED: 'participant_verification_completed',
  PARTICIPANT_DATA_SOURCE_SELECTED: 'participant_data_source_selected',

  // Fitbit authorization events
  PARTICIPANT_FITBIT_AUTHORIZED: 'participant_fitbit_authorized',
  PARTICIPANT_FITBIT_AUTH_FAILED: 'participant_fitbit_auth_failed',
  PARTICIPANT_FITBIT_REAUTH_INITIATED: 'participant_fitbit_reauth_initiated',
  PARTICIPANT_FITBIT_REAUTHORIZED: 'participant_fitbit_reauthorized',

  // HealthKit linking events
  PARTICIPANT_QRCODE_GENERATED: 'participant_qrcode_generated',
  PARTICIPANT_HEALTHKIT_LINKED: 'participant_healthkit_linked',
  PARTICIPANT_FALLBACK_CODE_REQUESTED: 'participant_fallback_code_requested',

  // Enrollment completion
  PARTICIPANT_ENROLLMENT_COMPLETED: 'participant_enrollment_completed',
} as const;

// Type definitions for event properties
interface UserIdentifyProperties {
  role: 'master' | 'researcher' | 'participant';
  status?: 'noprofile' | 'pending' | 'approved' | 'rejected';
  institution?: string;
  isMasterAccount?: boolean;
}

interface StudyCreatedProperties {
  studyId: string;
  collaboratorCount: number;
  hasDuration: boolean;
}

interface StudyCodeCopiedProperties {
  studyId: string;
  source: 'dashboard' | 'study_detail';
}

interface ParticipantsInvitedProperties {
  studyId: string;
  participantCount: number;
  emailMode: string;
}

interface CollaboratorsInvitedProperties {
  studyId: string;
  recipientCount: number;
}

interface ParticipantDeletedProperties {
  studyId: string;
}

interface TrackingIdEditedProperties {
  studyId: string;
  hasTrackingId: boolean;
}

interface CsvExportProperties {
  filterStatus: string;
  hasSearchQuery: boolean;
}

interface ExtractionDataSourceSelectedProperties {
  dataSource: 'fitbit' | 'healthkit';
}

interface ExtractionDateRangeSelectedProperties {
  mode: 'single' | 'range';
  startDate: string;
  endDate?: string;
}

interface ExtractionParticipantsSelectedProperties {
  participantCount: number;
  selectAll: boolean;
}

interface ExtractionDataTypesSelectedProperties {
  dataTypes: string[];
  count: number;
}

interface ExtractionPreviewOpenedProperties {
  participantCount: number;
  dataTypeCount: number;
}

interface ExtractionStartedProperties {
  studyId: string;
  participantCount: number;
  dataSource: 'fitbit' | 'healthkit';
  dataTypes: string[];
  dateRangeMode: 'single' | 'range';
}

interface ExtractionJobCompletedProperties {
  jobId: string;
  participantId: string;
}

interface ExtractionJobFailedProperties {
  jobId: string;
  error: string;
}

interface ExtractionBucketAccessedProperties {
  studyId: string;
}

interface ParticipantStudyCodeEnteredProperties {
  success: boolean;
}

interface ParticipantContactSubmittedProperties {
  hasPhone: boolean;
}

interface ParticipantVerificationSentProperties {
  method: 'email' | 'phone';
}

interface ParticipantVerificationCompletedProperties {
  success: boolean;
}

interface ParticipantDataSourceSelectedProperties {
  dataSource: 'fitbit' | 'healthkit';
}

interface ParticipantFitbitAuthorizedProperties {
  success: boolean;
}

interface ParticipantFitbitAuthFailedProperties {
  error: string;
}

interface ParticipantFitbitReauthInitiatedProperties {
  participantId: string;
}

interface ParticipantFitbitReauthorizedProperties {
  success: boolean;
}

interface ParticipantQrcodeGeneratedProperties {
  participantId: string;
}

interface ParticipantHealthkitLinkedProperties {
  linkingMethod: 'websocket' | 'polling';
}

interface ParticipantFallbackCodeRequestedProperties {
  participantId: string;
}

interface ParticipantEnrollmentCompletedProperties {
  dataSource: 'fitbit' | 'healthkit';
  hasDocuments: boolean;
}

/**
 * Analytics service for PostHog integration
 */
export const analyticsService = {
  /**
   * Identify a user after authentication
   */
  identifyUser: (userId: string, properties: UserIdentifyProperties) => {
    if (!userId) return;

    posthog.identify(userId, {
      role: properties.role,
      status: properties.status,
      institution: properties.institution,
      is_master_account: properties.isMasterAccount,
    });
  },

  /**
   * Reset user identification on logout
   */
  resetUser: () => {
    posthog.reset();
  },

  // ============================================
  // Authentication Events
  // ============================================

  trackSignIn: (method: 'email' | 'google') => {
    posthog.capture(AnalyticsEvents.USER_SIGNED_IN, { method });
  },

  trackSignUp: (method: 'email' | 'google') => {
    posthog.capture(AnalyticsEvents.USER_SIGNED_UP, { method });
  },

  trackSignOut: () => {
    posthog.capture(AnalyticsEvents.USER_SIGNED_OUT);
  },

  // ============================================
  // Study Management Events
  // ============================================

  trackStudyCreated: (properties: StudyCreatedProperties) => {
    posthog.capture(AnalyticsEvents.STUDY_CREATED, {
      study_id: properties.studyId,
      collaborator_count: properties.collaboratorCount,
      has_duration: properties.hasDuration,
    });
  },

  trackStudyCodeCopied: (properties: StudyCodeCopiedProperties) => {
    posthog.capture(AnalyticsEvents.STUDY_CODE_COPIED, {
      study_id: properties.studyId,
      source: properties.source,
    });
  },

  trackParticipantsInvited: (properties: ParticipantsInvitedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANTS_INVITED, {
      study_id: properties.studyId,
      participant_count: properties.participantCount,
      email_mode: properties.emailMode,
    });
  },

  trackCollaboratorsInvited: (properties: CollaboratorsInvitedProperties) => {
    posthog.capture(AnalyticsEvents.COLLABORATORS_INVITED, {
      study_id: properties.studyId,
      recipient_count: properties.recipientCount,
    });
  },

  trackParticipantDeleted: (properties: ParticipantDeletedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_DELETED, {
      study_id: properties.studyId,
    });
  },

  trackTrackingIdEdited: (properties: TrackingIdEditedProperties) => {
    posthog.capture(AnalyticsEvents.TRACKING_ID_EDITED, {
      study_id: properties.studyId,
      has_tracking_id: properties.hasTrackingId,
    });
  },

  trackCsvExport: (properties: CsvExportProperties) => {
    posthog.capture(AnalyticsEvents.CSV_EXPORT_REQUESTED, {
      filter_status: properties.filterStatus,
      has_search_query: properties.hasSearchQuery,
    });
  },

  // ============================================
  // Data Extraction Events
  // ============================================

  trackExtractionDataSourceSelected: (properties: ExtractionDataSourceSelectedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_DATA_SOURCE_SELECTED, {
      data_source: properties.dataSource,
    });
  },

  trackExtractionDateRangeSelected: (properties: ExtractionDateRangeSelectedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_DATE_RANGE_SELECTED, {
      mode: properties.mode,
      start_date: properties.startDate,
      end_date: properties.endDate,
    });
  },

  trackExtractionParticipantsSelected: (properties: ExtractionParticipantsSelectedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_PARTICIPANTS_SELECTED, {
      participant_count: properties.participantCount,
      select_all: properties.selectAll,
    });
  },

  trackExtractionDataTypesSelected: (properties: ExtractionDataTypesSelectedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_DATA_TYPES_SELECTED, {
      data_types: properties.dataTypes,
      count: properties.count,
    });
  },

  trackExtractionPreviewOpened: (properties: ExtractionPreviewOpenedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_PREVIEW_OPENED, {
      participant_count: properties.participantCount,
      data_type_count: properties.dataTypeCount,
    });
  },

  trackExtractionStarted: (properties: ExtractionStartedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_STARTED, {
      study_id: properties.studyId,
      participant_count: properties.participantCount,
      data_source: properties.dataSource,
      data_types: properties.dataTypes,
      date_range_mode: properties.dateRangeMode,
    });
  },

  trackExtractionJobCompleted: (properties: ExtractionJobCompletedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_JOB_COMPLETED, {
      job_id: properties.jobId,
      participant_id: properties.participantId,
    });
  },

  trackExtractionJobFailed: (properties: ExtractionJobFailedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_JOB_FAILED, {
      job_id: properties.jobId,
      error: properties.error,
    });
  },

  trackExtractionBucketAccessed: (properties: ExtractionBucketAccessedProperties) => {
    posthog.capture(AnalyticsEvents.EXTRACTION_BUCKET_ACCESSED, {
      study_id: properties.studyId,
    });
  },

  // ============================================
  // Participant Enrollment Events
  // ============================================

  trackParticipantStudyCodeEntered: (properties: ParticipantStudyCodeEnteredProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_STUDY_CODE_ENTERED, {
      success: properties.success,
    });
  },

  trackParticipantContactSubmitted: (properties: ParticipantContactSubmittedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_CONTACT_SUBMITTED, {
      has_phone: properties.hasPhone,
    });
  },

  trackParticipantVerificationSent: (properties: ParticipantVerificationSentProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_VERIFICATION_SENT, {
      method: properties.method,
    });
  },

  trackParticipantVerificationCompleted: (properties: ParticipantVerificationCompletedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_VERIFICATION_COMPLETED, {
      success: properties.success,
    });
  },

  trackParticipantDataSourceSelected: (properties: ParticipantDataSourceSelectedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_DATA_SOURCE_SELECTED, {
      data_source: properties.dataSource,
    });
  },

  // ============================================
  // Fitbit Authorization Events
  // ============================================

  trackParticipantFitbitAuthorized: (properties: ParticipantFitbitAuthorizedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_FITBIT_AUTHORIZED, {
      success: properties.success,
    });
  },

  trackParticipantFitbitAuthFailed: (properties: ParticipantFitbitAuthFailedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_FITBIT_AUTH_FAILED, {
      error: properties.error,
    });
  },

  trackParticipantFitbitReauthInitiated: (properties: ParticipantFitbitReauthInitiatedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_FITBIT_REAUTH_INITIATED, {
      participant_id: properties.participantId,
    });
  },

  trackParticipantFitbitReauthorized: (properties: ParticipantFitbitReauthorizedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_FITBIT_REAUTHORIZED, {
      success: properties.success,
    });
  },

  // ============================================
  // HealthKit Linking Events
  // ============================================

  trackParticipantQrcodeGenerated: (properties: ParticipantQrcodeGeneratedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_QRCODE_GENERATED, {
      participant_id: properties.participantId,
    });
  },

  trackParticipantHealthkitLinked: (properties: ParticipantHealthkitLinkedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_HEALTHKIT_LINKED, {
      linking_method: properties.linkingMethod,
    });
  },

  trackParticipantFallbackCodeRequested: (properties: ParticipantFallbackCodeRequestedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_FALLBACK_CODE_REQUESTED, {
      participant_id: properties.participantId,
    });
  },

  // ============================================
  // Enrollment Completion
  // ============================================

  trackParticipantEnrollmentCompleted: (properties: ParticipantEnrollmentCompletedProperties) => {
    posthog.capture(AnalyticsEvents.PARTICIPANT_ENROLLMENT_COMPLETED, {
      data_source: properties.dataSource,
      has_documents: properties.hasDocuments,
    });
  },
};

export default analyticsService;
